import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { T, US_STATES, familyColor } from '../tokens';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// ─── Types ────────────────────────────────────────────────────────────────────

interface ButterflySpecies {
  id: number;
  commonName: string;
  scientificName: string;
  commonFamilyName: string;
  scientificFamilyName: string;
  commonSubfamilyName: string;
  scientificSubfamilyName: string;
}

interface ButterflySighting {
  surveyId: number;
  speciesId: number;
  total: number;
  sectionSightings: string[] | null;
  surveyDate: string;
  location: string;
  weatherStart: any;
  weatherEnd: any;
  butterflyName: string;
  scientificName: string;
  familyName: string;
  commonFamilyName: string;
  scientificFamilyName: string;
  commonSubfamilyName: string;
  scientificSubfamilyName: string;
}

interface FilterData {
  dateRange: { start: string; end: string };
  selectedStates: string[];
  selectedSpecies: number[];
  weatherCondition: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────

const ArrowLeft = () => (
  <svg width={22} height={22} viewBox="0 0 24 24" fill="none">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FilterIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none">
    <path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── Shared atoms ─────────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontFamily: T.font, fontSize: 11, fontWeight: 600, color: T.ink3,
      letterSpacing: '0.08em', textTransform: 'uppercase', margin: '20px 0 8px',
    }}>
      {children}
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div style={{
      background: T.card, border: `1px solid ${T.line}`,
      borderRadius: T.r.md, padding: '14px 12px',
      boxShadow: T.shadowCard,
    }}>
      <div style={{
        fontFamily: T.fontDisplay, fontSize: 26, fontWeight: 700,
        color: accent ?? T.ink, letterSpacing: '-0.03em', lineHeight: 1,
      }}>
        {value}
      </div>
      <div style={{ fontFamily: T.font, fontSize: 11, color: T.ink2, marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

// ─── Chart palette ────────────────────────────────────────────────────────────

const PIE_PALETTE = [
  '#2D5016', '#D9962E', '#A8462B', '#4A7C59', '#8B6914',
  '#3D6B8C', '#7A5C3A', '#5E4A7A', '#2E6B5E', '#8C4A2E',
];

const WEATHER_GROUPS = [
  { label: 'Sunny / Clear', terms: ['sunny', 'clear', 'fair'] },
  { label: 'Partly Cloudy', terms: ['partly cloudy', 'partly sunny', 'few clouds', 'scattered clouds'] },
  { label: 'Cloudy / Overcast', terms: ['cloudy', 'mostly cloudy', 'overcast', 'broken clouds'] },
  { label: 'Rain', terms: ['rain', 'drizzle', 'shower', 'sprinkle'] },
  { label: 'Storms', terms: ['storm', 'thunder', 'lightning'] },
  { label: 'Snow / Ice', terms: ['snow', 'sleet', 'ice', 'freezing', 'flurries'] },
  { label: 'Fog / Haze', terms: ['fog', 'haze', 'mist', 'smoke'] },
  { label: 'Windy', terms: ['wind', 'breezy', 'gust'] },
];

const normalizeDate = (value: string | null | undefined) => {
  if (!value) return '';
  return new Date(value).toISOString().split('T')[0];
};

const parseWeather = (value: string | null | undefined) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getWeatherDescription = (weather: any) => {
  if (!weather || typeof weather !== 'object') return '';
  return String(weather.description ?? weather.text ?? '').trim();
};

const getWeatherGroup = (description: string) => {
  const normalized = description.toLowerCase();
  return WEATHER_GROUPS.find(group => group.terms.some(term => normalized.includes(term)))?.label ?? description;
};

const isUnknownSpeciesName = (name: string) => !name || name.toLowerCase() === 'unknown';

const taxonomyPair = (commonName: string, scientificName: string) =>
  [commonName || 'Unknown', scientificName || 'Unknown'].join(' / ');

const analyticsSpeciesLabel = (sp: {
  name: string;
  commonFamily: string;
  scientificFamily: string;
  commonSubfamily: string;
  scientificSubfamily: string;
}) => {
  if (!isUnknownSpeciesName(sp.name)) return sp.name;
  const shortName = sp.commonSubfamily || sp.commonFamily || sp.scientificSubfamily || sp.scientificFamily || 'Unknown';
  return `UK/${shortName}`;
};

// ─── Main component ───────────────────────────────────────────────────────────

const AnalyticsPage = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const navigate = useNavigate();
  const [sightings, setSightings] = useState<ButterflySighting[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FilterData>({
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0],
    },
    selectedStates: [],
    selectedSpecies: [],
    weatherCondition: '',
  });

  // ── Data fetching (unchanged logic) ────────────────────────────────────────

  useEffect(() => {
    if(!user) {
        localStorage.clear();
        navigate("/");
    } 

    if(!user?.admin) {
      navigate("/menu");
    }

    const loadAnalytics = async () => {
      try {
        const butterflyRes = await fetch('api/butterflies');
        const butterflyData = await butterflyRes.json();
        const butterflyMap = new Map<number, ButterflySpecies>();
        butterflyData.forEach((b: ButterflySpecies) => butterflyMap.set(b.id, b));

        const surveysRes = await fetch('api/surveys');
        const surveys = await surveysRes.json();

        const detailPromises = surveys.map(async (survey: any) => {
          try {
            const detailsRes = await fetch(`api/surveys/${survey.id}/details`);
            if (!detailsRes.ok) return [];
            const detailsData = await detailsRes.json();
            return detailsData.map((detail: any) => {
              const speciesId = detail.id?.speciesId ?? detail.butterfly?.id ?? 0;
              const butterflyInfo = butterflyMap.get(speciesId) ?? detail.butterfly;
              return {
                surveyId: survey.id,
                speciesId,
                total: detail.total ?? 0,
                sectionSightings: detail.sectionSightings ?? null,
                surveyDate: normalizeDate(survey.surveyDate),
                location: survey.location ?? 'Unknown',
                weatherStart: parseWeather(survey.weatherStart),
                weatherEnd: parseWeather(survey.weatherEnd),
                butterflyName: butterflyInfo?.commonName ?? `Species ${speciesId}`,
                scientificName: butterflyInfo?.scientificName ?? 'Unknown',
                familyName: butterflyInfo?.commonFamilyName ?? butterflyInfo?.scientificFamilyName ?? 'Unknown',
                commonFamilyName: butterflyInfo?.commonFamilyName ?? 'Unknown',
                scientificFamilyName: butterflyInfo?.scientificFamilyName ?? 'Unknown',
                commonSubfamilyName: butterflyInfo?.commonSubfamilyName ?? 'Unknown',
                scientificSubfamilyName: butterflyInfo?.scientificSubfamilyName ?? 'Unknown',
              };
            });
          } catch {
            return [];
          }
        });

        const allSightings = (await Promise.all(detailPromises)).flat();
        setSightings(allSightings);
      } catch (err) {
        console.error('Failed to fetch analytics data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, []);

  // ── Derived data (unchanged logic) ────────────────────────────────────────

  const filteredSightings = useMemo(() => sightings.filter(s => {
    if (!s.surveyDate) return false;
    if (s.surveyDate < filters.dateRange.start || s.surveyDate > filters.dateRange.end) return false;
    if (filters.selectedStates.length > 0 && !filters.selectedStates.includes(s.location)) return false;
    if (filters.selectedSpecies.length > 0 && !filters.selectedSpecies.includes(s.speciesId)) return false;
    if (filters.weatherCondition) {
      const descriptions = [getWeatherDescription(s.weatherStart), getWeatherDescription(s.weatherEnd)];
      if (!descriptions.some(desc => getWeatherGroup(desc) === filters.weatherCondition)) return false;
    }
    return true;
  }), [sightings, filters]);

  const totalButterflies = useMemo(() =>
    filteredSightings.reduce((sum, s) => sum + (s.total || 0), 0),
    [filteredSightings]);

  const top10Species = useMemo(() => {
    const map = new Map<number, {
      id: number;
      name: string;
      scientific: string;
      family: string;
      commonFamily: string;
      scientificFamily: string;
      commonSubfamily: string;
      scientificSubfamily: string;
      total: number;
    }>();
    filteredSightings.forEach(s => {
      const ex = map.get(s.speciesId);
      if (ex) { ex.total += s.total || 0; }
      else map.set(s.speciesId, {
        id: s.speciesId,
        name: s.butterflyName,
        scientific: s.scientificName,
        family: s.familyName,
        commonFamily: s.commonFamilyName,
        scientificFamily: s.scientificFamilyName,
        commonSubfamily: s.commonSubfamilyName,
        scientificSubfamily: s.scientificSubfamilyName,
        total: s.total || 0,
      });
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [filteredSightings]);

  const butterfliesByState = useMemo(() => {
    const map = new Map<string, number>();
    filteredSightings.forEach(s => {
      const st = s.location || 'Unknown';
      map.set(st, (map.get(st) || 0) + (s.total || 0));
    });
    return Array.from(map.entries())
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => b.count - a.count);
  }, [filteredSightings]);

  const uniqueStates = useMemo(() =>
    Array.from(new Set(sightings.map(s => s.location).filter(Boolean))).sort(),
    [sightings]);

  const uniqueSpecies = useMemo(() => {
    const map = new Map<number, { id: number; name: string; scientific: string }>();
    sightings.forEach(s => {
      if (s.speciesId) map.set(s.speciesId, {
        id: s.speciesId,
        name: analyticsSpeciesLabel({
          name: s.butterflyName,
          commonFamily: s.commonFamilyName,
          scientificFamily: s.scientificFamilyName,
          commonSubfamily: s.commonSubfamilyName,
          scientificSubfamily: s.scientificSubfamilyName,
        }),
        scientific: s.scientificName,
      });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [sightings]);

  const uniqueWeatherConditions = useMemo(() =>
    Array.from(new Set(
      sightings.flatMap(s => [getWeatherDescription(s.weatherStart), getWeatherDescription(s.weatherEnd)])
        .filter(Boolean)
        .map(getWeatherGroup)
    )).sort((a, b) => a.localeCompare(b)),
    [sightings]);

  const calculateScale = (max: number) => max > 10000 ? 100 : max > 1000 ? 10 : 1;
  const top10Scale = useMemo(() => calculateScale(top10Species.reduce((m, i) => Math.max(m, i.total), 0)), [top10Species]);

  // ── Filter handlers ────────────────────────────────────────────────────────

  const handleStateToggle = (state: string) =>
    setFilters(prev => ({
      ...prev,
      selectedStates: prev.selectedStates.includes(state)
        ? prev.selectedStates.filter(s => s !== state)
        : [...prev.selectedStates, state],
    }));

  const handleSpeciesToggle = (id: number) =>
    setFilters(prev => ({
      ...prev,
      selectedSpecies: prev.selectedSpecies.includes(id)
        ? prev.selectedSpecies.filter(s => s !== id)
        : [...prev.selectedSpecies, id],
    }));

  const resetFilters = () => setFilters({
    dateRange: {
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      end: new Date().toISOString().split('T')[0],
    },
    selectedStates: [],
    selectedSpecies: [],
    weatherCondition: '',
  });

  const activeFilterCount =
    (filters.selectedStates.length > 0 ? 1 : 0) +
    (filters.selectedSpecies.length > 0 ? 1 : 0) +
    (filters.weatherCondition ? 1 : 0);

  // ── Chart configs ──────────────────────────────────────────────────────────

  const sharedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: T.ink,
        titleFont: { family: T.fontDisplay, size: 13 },
        bodyFont: { family: T.font, size: 12 },
        padding: 10,
        callbacks: {
          label: (ctx: any) => {
            const v = typeof ctx.raw === 'number' ? ctx.raw : 0;
            const scale = ctx.dataset.label?.includes('(x') ? Number(ctx.dataset.label.match(/x(\d+)/)?.[1] ?? 1) : 1;
            return `  ${(v * scale).toLocaleString()} butterflies`;
          },
        },
      },
    },
  };

  const top10ChartData = {
    labels: top10Species.map(i => analyticsSpeciesLabel(i)),
    datasets: [{
      label: top10Scale > 1 ? `Butterflies Seen (x${top10Scale})` : 'Butterflies Seen',
      data: top10Species.map(i => Math.round(i.total / top10Scale)),
      backgroundColor: top10Species.map(i => familyColor(i.family) + 'CC'),
      borderColor: top10Species.map(i => familyColor(i.family)),
      borderWidth: 1,
      borderRadius: 4,
    }],
  };

  const pieChartData = {
    labels: top10Species.map(i => analyticsSpeciesLabel(i)),
    datasets: [{
      data: top10Species.map(i => i.total),
      backgroundColor: PIE_PALETTE.slice(0, top10Species.length),
      borderColor: T.bg,
      borderWidth: 2,
    }],
  };

  const stateChartData = {
    labels: butterfliesByState.map(i => i.state),
    datasets: [{
      label: 'Butterflies Seen',
      data: butterfliesByState.map(i => i.count),
      backgroundColor: 'rgba(217,150,46,0.75)',
      borderColor: T.honey,
      borderWidth: 1,
      borderRadius: 3,
    }],
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="lep-page">
        <div className="lep-page-inner" style={{ alignItems: 'center', justifyContent: 'center', padding: '0 20px' }}>
          <div style={{ fontFamily: T.font, fontSize: 15, color: T.ink3, textAlign: 'center', paddingTop: 120 }}>
            Loading analytics…
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lep-page">
      <div className="lep-page-inner" style={{ padding: '0 0 48px' }}>

        {/* Header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          background: T.bg, borderBottom: `1px solid ${T.line}`,
          padding: '54px 20px 16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => navigate('/menu')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink2, padding: 4, margin: -4, display: 'flex' }}
            >
              <ArrowLeft />
            </button>
            <div style={{ flex: 1, fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 600, color: T.ink, letterSpacing: '-0.02em' }}>
              Analytics
            </div>
            <button
              onClick={() => setFiltersOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontFamily: T.font, fontSize: 13, fontWeight: 500,
                color: activeFilterCount > 0 ? T.forest : T.ink2,
                background: activeFilterCount > 0 ? T.forestSoft : T.bgDeep,
                border: `1px solid ${activeFilterCount > 0 ? T.forest + '40' : T.line}`,
                borderRadius: T.r.pill, padding: '6px 12px', cursor: 'pointer',
              }}
            >
              <FilterIcon />
              Filters
              {activeFilterCount > 0 && (
                <span style={{
                  background: T.forest, color: '#fff', borderRadius: '50%',
                  width: 16, height: 16, fontSize: 10, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {activeFilterCount}
                </span>
              )}
              <ChevronDown open={filtersOpen} />
            </button>
          </div>
        </div>

        <div style={{ padding: '0 20px' }}>

          {/* Filters panel */}
          {filtersOpen && (
            <div style={{
              marginTop: 16, background: T.card, border: `1px solid ${T.line}`,
              borderRadius: T.r.lg, padding: 16, boxShadow: T.shadowCard,
            }}>
              {/* Date range */}
              <SectionLabel>Date range</SectionLabel>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  ['From', 'start', filters.dateRange.start],
                  ['To', 'end', filters.dateRange.end],
                ].map(([label, key, val]) => (
                  <div key={key as string} style={{ flex: 1 }}>
                    <div style={{ fontFamily: T.font, fontSize: 12, color: T.ink2, marginBottom: 4 }}>{label}</div>
                    <input
                      type="date"
                      value={val as string}
                      onChange={e => setFilters(prev => ({ ...prev, dateRange: { ...prev.dateRange, [key as string]: e.target.value } }))}
                      style={{
                        width: '100%', boxSizing: 'border-box', height: 40, padding: '0 10px',
                        fontFamily: T.font, fontSize: 14, color: T.ink,
                        background: T.bg, border: `1px solid ${T.line}`,
                        borderRadius: T.r.sm, outline: 'none',
                      }}
                    />
                  </div>
                ))}
              </div>

              {/* Weather */}
              <SectionLabel>Weather condition</SectionLabel>
              <select
                value={filters.weatherCondition}
                onChange={e => setFilters(prev => ({ ...prev, weatherCondition: e.target.value }))}
                style={{
                  width: '100%', boxSizing: 'border-box', height: 40, padding: '0 12px',
                  fontFamily: T.font, fontSize: 14, color: filters.weatherCondition ? T.ink : T.ink3,
                  background: T.bg, border: `1px solid ${T.line}`,
                  borderRadius: T.r.sm, outline: 'none',
                }}
              >
                <option value="">All weather conditions</option>
                {uniqueWeatherConditions.map(condition => (
                  <option key={condition} value={condition}>{condition}</option>
                ))}
              </select>

              {/* States */}
              {uniqueStates.length > 0 && (
                <>
                  <SectionLabel>States ({uniqueStates.length})</SectionLabel>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {uniqueStates.map(state => {
                      const active = filters.selectedStates.includes(state);
                      return (
                        <button key={state} onClick={() => handleStateToggle(state)} style={{
                          fontFamily: T.font, fontSize: 12, fontWeight: 500,
                          color: active ? '#fff' : T.ink2,
                          background: active ? T.forest : T.bg,
                          border: `1px solid ${active ? T.forest : T.line}`,
                          borderRadius: T.r.pill, padding: '4px 10px', cursor: 'pointer',
                          transition: 'background 0.15s',
                        }}>
                          {state}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Species */}
              {uniqueSpecies.length > 0 && (
                <>
                  <SectionLabel>Species ({uniqueSpecies.length})</SectionLabel>
                  <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {uniqueSpecies.map(sp => {
                      const active = filters.selectedSpecies.includes(sp.id);
                      return (
                        <button key={sp.id} onClick={() => handleSpeciesToggle(sp.id)} style={{
                          width: '100%', textAlign: 'left', background: active ? T.forestSoft : 'none',
                          border: `1px solid ${active ? T.forest + '40' : 'transparent'}`,
                          borderRadius: T.r.sm, padding: '6px 10px', cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                          <div style={{
                            width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                            border: `1.5px solid ${active ? T.forest : T.lineStrong}`,
                            background: active ? T.forest : 'none',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {active && <svg width={9} height={9} viewBox="0 0 10 10"><path d="M2 5l2.5 2.5L8 2" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <span style={{ fontFamily: T.font, fontSize: 13, color: T.ink }}>{sp.name}</span>
                          <span style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>{sp.scientific}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Reset */}
              <button
                onClick={resetFilters}
                style={{
                  marginTop: 14, width: '100%', height: 40,
                  fontFamily: T.font, fontSize: 14, fontWeight: 500, color: T.ink2,
                  background: T.bgDeep, border: `1px solid ${T.line}`,
                  borderRadius: T.r.md, cursor: 'pointer',
                }}
              >
                Reset filters
              </button>
            </div>
          )}

          {/* Stat cards */}
          <SectionLabel>Overview</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <StatCard label="butterflies" value={totalButterflies.toLocaleString()} accent={T.forest} />
            <StatCard label="surveys" value={new Set(filteredSightings.map(s => s.surveyId)).size} />
            <StatCard label="species" value={new Set(filteredSightings.map(s => s.speciesId)).size} accent={T.honey} />
            <StatCard label="states" value={new Set(filteredSightings.map(s => s.location)).size} />
          </div>

          {filteredSightings.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 0', fontFamily: T.font, fontSize: 15, color: T.ink3 }}>
              No sightings match the current filters
            </div>
          ) : (
            <>
              {/* Top 10 bar chart */}
              <SectionLabel>Top 10 species</SectionLabel>
              <div style={{
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: T.r.lg, padding: 16, boxShadow: T.shadowCard,
              }}>
                <div style={{ height: 240 }}>
                  <Bar
                    data={top10ChartData}
                    options={{
                      ...sharedChartOptions,
                      scales: {
                        y: { beginAtZero: true, grid: { color: T.line }, ticks: { font: { family: T.font, size: 11 }, color: T.ink3 } },
                        x: { grid: { display: false }, ticks: { font: { family: T.font, size: 10 }, color: T.ink2, maxRotation: 30 } },
                      },
                    }}
                  />
                </div>
              </div>

              {/* Pie chart */}
              <SectionLabel>Species breakdown</SectionLabel>
              <div style={{
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: T.r.lg, padding: 16, boxShadow: T.shadowCard,
              }}>
                <div style={{ height: 240 }}>
                  <Pie
                    data={{
                      ...pieChartData,
                      datasets: [{
                        ...pieChartData.datasets[0],
                      }],
                    }}
                    options={{
                      ...sharedChartOptions,
                      plugins: {
                        ...sharedChartOptions.plugins,
                        legend: {
                          display: true,
                          position: 'bottom' as const,
                          labels: {
                            font: { family: T.font, size: 11 },
                            color: T.ink2,
                            boxWidth: 10,
                            padding: 10,
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>

              {/* State bar chart */}
              {butterfliesByState.length > 0 && (
                <>
                  <SectionLabel>By state</SectionLabel>
                  <div style={{
                    background: T.card, border: `1px solid ${T.line}`,
                    borderRadius: T.r.lg, padding: 16, boxShadow: T.shadowCard,
                  }}>
                    <div style={{ height: Math.max(180, butterfliesByState.length * 28) }}>
                      <Bar
                        data={stateChartData}
                        options={{
                          ...sharedChartOptions,
                          indexAxis: 'y' as const,
                          scales: {
                            x: { beginAtZero: true, grid: { color: T.line }, ticks: { font: { family: T.font, size: 11 }, color: T.ink3 } },
                            y: { grid: { display: false }, ticks: { font: { family: T.font, size: 12 }, color: T.ink2 } },
                          },
                        }}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Species table */}
              <SectionLabel>Top 10 species — details</SectionLabel>
              <div style={{
                background: T.card, border: `1px solid ${T.line}`,
                borderRadius: T.r.lg, overflow: 'hidden', boxShadow: T.shadowCard,
              }}>
                {top10Species.map((sp, idx) => {
                  const isUnknown = isUnknownSpeciesName(sp.name);
                  return (
                    <div key={sp.id} style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '11px 16px',
                      borderBottom: idx < top10Species.length - 1 ? `1px solid ${T.line}` : 'none',
                      background: idx % 2 === 0 ? T.card : T.bg,
                    }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: T.r.sm, flexShrink: 0,
                        background: familyColor(sp.family),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontFamily: T.fontMono, fontSize: 10, fontWeight: 700, color: '#fff',
                      }}>
                        {idx + 1}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 600, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {isUnknown ? analyticsSpeciesLabel(sp) : sp.name}
                        </div>
                        {isUnknown ? (
                          <>
                            <div style={{ fontFamily: T.font, fontSize: 11, color: T.ink3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {taxonomyPair(sp.commonFamily, sp.scientificFamily)}
                            </div>
                            <div style={{ fontFamily: T.font, fontSize: 11, color: T.ink3, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {taxonomyPair(sp.commonSubfamily, sp.scientificSubfamily)}
                            </div>
                          </>
                        ) : (
                          <div style={{ fontFamily: T.fontMono, fontSize: 11, color: T.ink3, fontStyle: 'italic' }}>
                            {sp.scientific}
                          </div>
                        )}
                      </div>
                      <div style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700, color: T.forest, letterSpacing: '-0.02em', flexShrink: 0 }}>
                        {sp.total.toLocaleString()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* State table */}
              {butterfliesByState.length > 0 && (
                <>
                  <SectionLabel>By state — details</SectionLabel>
                  <div style={{
                    background: T.card, border: `1px solid ${T.line}`,
                    borderRadius: T.r.lg, overflow: 'hidden', boxShadow: T.shadowCard, marginBottom: 8,
                  }}>
                    {butterfliesByState.map((row, idx) => {
                      const fullName = US_STATES.find(([c]) => c === row.state)?.[1] ?? row.state;
                      return (
                        <div key={row.state} style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '11px 16px',
                          borderBottom: idx < butterfliesByState.length - 1 ? `1px solid ${T.line}` : 'none',
                          background: idx % 2 === 0 ? T.card : T.bg,
                        }}>
                          <div style={{
                            width: 32, flexShrink: 0,
                            fontFamily: T.fontMono, fontSize: 11, fontWeight: 700,
                            color: T.ink3, textTransform: 'uppercase',
                          }}>
                            {row.state}
                          </div>
                          <div style={{ flex: 1, fontFamily: T.font, fontSize: 14, color: T.ink }}>
                            {fullName}
                          </div>
                          <div style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700, color: T.honey, letterSpacing: '-0.02em', flexShrink: 0 }}>
                            {row.count.toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
